import { Col, Row } from "reactstrap";
import { useCallback, useState } from "react";

import AsyncSelectField from "@components/fields/react-select/async-select";
import CommonModal from "@components/ui-components/modal";
import CustomFieldsRenderer from "@src/views/panel/custom-fields/misc/custom-fields-renderer";
import FormView from "@components/views/form-view";
import InputField from "@components/fields/input-field";
import { STATUS_OPTIONS } from "../../../misc/constants";
import Select from "@components/fields/react-select/select";
import SingleDatePicker from "@components/fields/date-picker/single-picker";
import SwitchField from "@components/fields/switch-field";
import useFilters from "@src/views/common/hooks/use-filter";
import { useParams } from "react-router-dom";
import useUpsert from "./use-upsert";
import { useWatch } from "react-hook-form";

const Upsert = (props) => {
    const {
        handleModal,
        modalConfig: { isOpen, row = {} }
    } = props;

    const { onSubmit, isDirty, loading, methods, customFields } =
    useUpsert(props);
    const [onboardingChurnId, setOnboardingChurnId] = useState(null);

    const isEdit = !!row._id;
    const { loadAsyncOptions } = useFilters();

    const {
        control,
        watch,
        formState: { errors },
        setValue
    } = methods;

    const isChurn = useWatch({ control, name: "is_churn" });
    const stage = useWatch({ control, name: "stage" });
    const shouldDisableChurnToggle = stage?.value === onboardingChurnId;
    const isAdd = !row._id;
    const { platformId } = useParams();
    const isParentCompany = watch("is_p_comp");
    const parentCompany = watch("p_comp");

    const getParentCompanyVisibility = () => !isParentCompany && (!isAdd ? !!parentCompany : true);

    const getMRREditable = () => !isParentCompany && !!parentCompany && parentCompany !== "N/A";

    const PlatformFields = useCallback(() => {
        return (
            <>
                <Col lg={4}>
                    <AsyncSelectField
                        key={`csm-${platformId}`}
                        loadOptions={(value, callback) => loadAsyncOptions({
                            value,
                            callback,
                            type       : "csm",
                            context    : {},
                            statusCheck: true
                        })
                        }
                        defaultOptions
                        id="csm"
                        isClearable
                        label="CSM"
                        name="csm"
                        required
                        control={control}
                        errors={errors}/>
                </Col>

                <Col lg={4}>
                    <AsyncSelectField
                        key={`sdr-${platformId}`}
                        loadOptions={(value, callback) => loadAsyncOptions({
                            value,
                            callback,
                            type       : "sdr",
                            context    : {},
                            statusCheck: true
                        })
                        }
                        defaultOptions
                        id="sdr"
                        isClearable
                        label="SDR"
                        name="sdr"
                        required
                        control={control}
                        errors={errors}/>
                </Col>

                <Col lg={4}>
                    <SwitchField
                        label="Is Parent Company"
                        name="is_p_comp"
                        customOnChange={() => {
                            setValue("p_comp", "");
                        }}
                        control={control}
                        tooltipContent="Select the toggle to mark as a parent company"/>
                </Col>

                {!isParentCompany && (
                    <Col lg={4}>
                        <AsyncSelectField
                            loadOptions={(value, callback) => loadAsyncOptions({
                                value,
                                callback: (options) => {
                                    const filtered = options.filter(
                                        (opt) => opt.value !== row._id
                                    );
                                    callback(filtered);
                                },
                                type   : "parents",
                                context: {
                                    platform_id: platformId
                                },
                                statusCheck: true
                            })
                            }
                            defaultOptions
                            id="p_comp"
                            isClearable
                            label="Parent Company"
                            name="p_comp"
                            control={control}
                            errors={errors}/>
                    </Col>
                )}
            </>
        );
    }, [platformId, getParentCompanyVisibility]);

    const content = [
        {
            leftChild: {
                title: "Basic Details",
                desc : "Enter basic company details"
            },
            rightChild: (
                <Row>
                    <Col lg={6}>
                        <InputField
                            id="company_name"
                            name="company_name"
                            label="Name"
                            type="text"
                            control={control}
                            errors={errors}
                            required/>
                    </Col>
                    {PlatformFields()}
                </Row>
            )
        },
        {
            leftChild: {
                title: "Misc Details",
                desc : "Enter other details of the company"
            },
            rightChild: (
                <Row>
                    <Col lg={4}>
                        <InputField
                            label="Internal ID"
                            id="cid"
                            name="cid"
                            control={control}
                            errors={errors}/>
                    </Col>

                    <Col>
                        <AsyncSelectField
                            // @ts-ignore
                            key={`stage-${platformId}`}
                            loadOptions={(value, callback) => {
                                loadAsyncOptions({
                                    value,
                                    callback: (options) => {
                                        const onboardingChurnOption = options.find(
                                            (opt) => opt.label === "Onboarding Churn"
                                        );
                                        if (onboardingChurnOption) {
                                            setOnboardingChurnId(onboardingChurnOption.value);
                                        }
                                        callback(options);
                                    },
                                    type       : "stages",
                                    context    : {},
                                    statusCheck: true
                                });
                            }}
                            defaultOptions
                            id="stage"
                            isClearable
                            label="Stage"
                            name="stage"
                            required
                            control={control}
                            errors={errors}
                            customOnChange={(selectedOption) => {
                                setValue("stage", selectedOption);
                                if (selectedOption?.value === onboardingChurnId) {
                                    setValue("is_churn", true);
                                }
                            }}/>
                    </Col>

                    <Col lg={4}>
                        <Select
                            label="Status"
                            control={control}
                            errors={errors}
                            id="status"
                            isClearable
                            required
                            name="status"
                            options={STATUS_OPTIONS}/>
                    </Col>

                    <Col lg={4}>
                        <InputField
                            disabled={getMRREditable()}
                            label="MRR"
                            type="text"
                            id="mrr"
                            name="mrr"
                            control={control}
                            errors={errors}
                            required/>
                    </Col>
                    <Col lg={4}>
                        <InputField
                            label="Billing Name"
                            type="text"
                            disabled
                            id="billing_name"
                            name="billing_name"
                            control={control}
                            errors={errors}/>
                    </Col>
                    <Col lg={4}>
                        <AsyncSelectField
                            loadOptions={(value, callback) => loadAsyncOptions({
                                value,
                                callback,
                                type: "plans"
                            })
                            }
                            defaultOptions
                            id="plan"
                            isClearable
                            label="Plan"
                            name="plan"
                            control={control}
                            errors={errors}/>
                    </Col>

                    <Col lg={4}>
                        <SingleDatePicker
                            label="Onboarding Won Date"
                            id="onboarding_won_date"
                            name="onboarding_won_date"
                            format="yyyy-MM-dd"
                            errors={errors}
                            control={control}/>
                    </Col>
                    <Col lg={4}>
                        <SingleDatePicker
                            label="Trial / Onboarding ongoing date"
                            id="trial_date"
                            format="yyyy-MM-dd"
                            name="trial_date"
                            errors={errors}
                            control={control}/>
                    </Col>

                    <Col lg={4}>
                        <SingleDatePicker
                            label="Created Date"
                            id="date"
                            name="date"
                            format="yyyy-MM-dd"
                            required
                            errors={errors}
                            control={control}/>
                    </Col>

                    <>
                        <Col lg={4}>
                            <SwitchField
                                id="is_churn"
                                name="is_churn"
                                label="Churn Status"
                                tooltipContent="Please select in case of churn"
                                control={control}
                                disabled={shouldDisableChurnToggle}/>
                        </Col>

                        {isChurn && (
                            <>
                                <Col lg={4}>
                                    <InputField
                                        id="r_churn"
                                        name="r_churn"
                                        label="Churn Reason"
                                        type="text"
                                        control={control}
                                        errors={errors}/>
                                </Col>

                                <Col lg={4}>
                                    <SingleDatePicker
                                        label="Churn Date"
                                        id="churned_at"
                                        name="churned_at"
                                        format="yyyy-MM-dd"
                                        control={control}
                                        errors={errors}
                                        required
                                        maxDate={new Date()}/>
                                </Col>
                            </>
                        )}
                    </>
                    <Col lg={4}>
                        <SingleDatePicker
                            label="Demo Date"
                            id="demo_date"
                            format="yyyy-MM-dd"
                            name="demo_date"
                            errors={errors}
                            control={control}/>
                    </Col>

                    <Col lg={4}>
                        <SingleDatePicker
                            label="Onboarding Invoice Raised Date"
                            id="invoice_raised_date"
                            format="yyyy-MM-dd"
                            name="invoice_raised_date"
                            errors={errors}
                            control={control}
                            maxDate={new Date()}/>
                    </Col>
                    <Col lg={4}>
                        <InputField
                            name="domain"
                            id="domain"
                            label="Domain"
                            control={control}
                            errors={errors}/>
                    </Col>
                    <Col lg={4}>
                        <AsyncSelectField
                            loadOptions={(value, callback) => loadAsyncOptions({
                                value,
                                callback,
                                type   : "tags",
                                context: {
                                    // @ts-ignore
                                    context: "client"
                                }
                            })
                            }
                            defaultOptions
                            id="tags"
                            isClearable
                            label="Tags"
                            isMulti
                            name="tags"
                            control={control}
                            errors={errors}/>
                    </Col>
                </Row>
            )
        },
        {
            leftChild: {
                title: "Custom Fields",
                desc : "Enter custom fields values"
            },
            rightChild: (
                <Row>
                    <CustomFieldsRenderer
                        fields={customFields}
                        control={control}
                        errors={errors}/>
                </Row>
            ),
            omit: customFields && !customFields.length
        }
    ];

    return (
        <CommonModal
            title={`${isEdit ? "Edit" : "Add"} Company`}
            body={<FormView content={content} />}
            confirmButton={{
                txt    : isEdit ? "Update" : "Add",
                icon   : "content-save",
                onClick: onSubmit,
                loading
            }}
            loading={loading}
            type="fullScreen"
            isDirty={isDirty}
            handleModal={handleModal}
            isOpen={isOpen}/>
    );
};

export default Upsert;
